import type {
  OnboardingField,
  OnboardingFormValues,
  OnboardingRequest,
  OnboardingRequestType,
  User,
} from "../types";

type ProfileForPrefill = Pick<
  User,
  "id" | "name" | "email" | "businessArea" | "division"
>;

export const ONBOARDING_STATUSES = [
  "submitted",
  "in-progress",
  "done",
  "declined",
] as const;

export function onboardingFields(type: OnboardingRequestType) {
  return type.groups.flatMap((group) => group.fields);
}

export function initialOnboardingValues(
  type: OnboardingRequestType,
  user: ProfileForPrefill | null | undefined,
  existing?: Record<string, unknown>,
): OnboardingFormValues {
  const values: OnboardingFormValues = {};
  for (const field of onboardingFields(type)) {
    const stored = existing?.[field.key];
    if (field.kind === "list") {
      values[field.key] = Array.isArray(stored) ? stored.map(String) : [];
      continue;
    }
    if (existing) {
      values[field.key] =
        stored === undefined || stored === null ? "" : String(stored);
      continue;
    }
    const prefilled = field.prefill ? user?.[field.prefill] : undefined;
    values[field.key] = prefilled ?? field.default ?? "";
  }
  return values;
}

const EMAIL_PATTERN = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

function validateText(field: OnboardingField, raw: string): string | null {
  const value = raw.trim();
  if (value === "") {
    return field.required ? `${field.label} is required` : null;
  }
  if (field.maxLength && value.length > field.maxLength) {
    return `${field.label} must be at most ${field.maxLength} characters`;
  }
  if (field.kind === "email" && !EMAIL_PATTERN.test(value)) {
    return `${field.label} must be a valid email address`;
  }
  if (field.pattern && !new RegExp(field.pattern).test(value)) {
    return field.help
      ? `${field.label}: ${field.help}`
      : `${field.label} must match ${field.pattern}`;
  }
  if (
    field.options &&
    field.options.length > 0 &&
    !field.options.some((o) => o.value === value)
  ) {
    return `${field.label} must be one of the offered options`;
  }
  return null;
}

function validateList(field: OnboardingField, raw: string[]): string | null {
  const entries = raw.map((s) => s.trim()).filter((s) => s !== "");
  if (entries.length === 0) {
    return field.required ? `${field.label} is required` : null;
  }
  if (field.pattern) {
    const pattern = new RegExp(field.pattern);
    if (entries.some((entry) => !pattern.test(entry))) {
      return `${field.label} entries must use the format organization/repository`;
    }
  }
  return null;
}

function validateNumber(field: OnboardingField, raw: string): string | null {
  const value = raw.trim();
  if (value === "") {
    return field.required ? `${field.label} is required` : null;
  }
  if (!/^-?\d+$/.test(value)) {
    return `${field.label} must be a whole number`;
  }
  if (field.minimum !== undefined && Number(value) < field.minimum) {
    return `${field.label} must be at least ${field.minimum}`;
  }
  return null;
}

export function validateOnboardingValues(
  type: OnboardingRequestType,
  values: OnboardingFormValues,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of onboardingFields(type)) {
    const raw = values[field.key];
    let error: string | null;
    if (field.kind === "list") {
      error = validateList(field, Array.isArray(raw) ? raw : []);
    } else if (field.kind === "number") {
      error = validateNumber(field, typeof raw === "string" ? raw : "");
    } else {
      error = validateText(field, typeof raw === "string" ? raw : "");
    }
    if (error) errors[field.key] = error;
  }
  return errors;
}

export function toOnboardingPayload(
  type: OnboardingRequestType,
  values: OnboardingFormValues,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of onboardingFields(type)) {
    const raw = values[field.key];
    if (field.kind === "list") {
      const entries = (Array.isArray(raw) ? raw : [])
        .map((s) => s.trim())
        .filter((s) => s !== "");
      if (entries.length > 0) payload[field.key] = entries;
      continue;
    }
    const text = typeof raw === "string" ? raw.trim() : "";
    if (text === "") continue;
    payload[field.key] = field.kind === "number" ? Number(text) : text;
  }
  return payload;
}

export function displayOnboardingValue(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "Not specified";
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? "Not specified" : value.join(", ");
  }
  return String(value);
}

export type OnboardingFilter = "open" | "closed";

export const ONBOARDING_FILTERS: { id: OnboardingFilter; label: string }[] = [
  { id: "open", label: "Open" },
  { id: "closed", label: "Closed" },
];

const CLOSED_STATUSES = new Set(["done", "declined"]);

export function isClosedOnboardingStatus(status: string): boolean {
  return CLOSED_STATUSES.has(status);
}

export function filterOnboardingRequests(
  requests: OnboardingRequest[],
  filter: OnboardingFilter,
): OnboardingRequest[] {
  if (filter === "closed") {
    return requests
      .filter((r) => isClosedOnboardingStatus(r.status))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }
  return requests.filter((r) => !isClosedOnboardingStatus(r.status));
}

export function countOnboardingRequests(
  requests: OnboardingRequest[],
): Record<OnboardingFilter, number> {
  const closed = requests.filter((r) =>
    isClosedOnboardingStatus(r.status),
  ).length;
  return { open: requests.length - closed, closed };
}

export function editedSinceStatusChange(request: OnboardingRequest): boolean {
  if (!request.editedAt || !request.statusChangedAt) return false;
  return new Date(request.editedAt) > new Date(request.statusChangedAt);
}
